import type { StaffRole } from './staff'

/**
 * Illustrative `StaffPermission` set + role→permission matrix used ONLY by
 * the SPA route guard for UX gating (hide/redirect nav a role can't use).
 *
 * This is NEVER the security boundary — admin-portal-specification.md §16.1
 * is explicit that authorization is enforced server-side; the Vue Router
 * `meta.permissions` guard + conditional UI is UX only. The authoritative
 * matrix lives in the Go backend's `staffauth.RequireStaffPermission`
 * (Contract C1) and is checked again per-action, independent of this file.
 *
 * Contract C1 defines `StaffPermission` as an open `string` type and
 * documents new permissions as additive/non-breaking. The `*:read`
 * permissions below extend C1's example set (`customer:read`,
 * `customer:reveal`, `billing:refund`, `entitlement:override`,
 * `ticket:reply`, `status:write`, `feature:write`) with one read permission
 * per MS-1 nav placeholder, so each section can be gated independently
 * before MS-2..MS-5 land their real, authoritative fine-grained matrices.
 *
 * `ticket:read`/`ticket:assign` were added alongside CRM-6's agent inbox
 * (TC2.1/TC2.2) — the real backend matrix
 * (`backend/internal/modules/staffauth/rbac.go`'s `permissionMatrix`)
 * already gates `GET .../tickets*` on `ticket:read` and
 * `POST .../assign`/`.../agent-pool/*` on `ticket:assign` (support_l2 +
 * admin_owner only — support_l1 can read + reply but NOT assign); this
 * illustrative client-side matrix had drifted from that (only `ticket:reply`
 * existed, and `support:read` — used for the nav/route guard — was doing
 * double duty for the queue-read gate too). Backfilled here so the inbox's
 * per-action UI gating (assign button, reply/note composer) matches the
 * server's real deny-by-default posture instead of only the coarse
 * route-level `support:read` guard.
 *
 * `help_article:read`/`help_article:write`/`help_article:publish` back
 * CRM-8's help-centre editor (Kirana-oj1u). `help_article:read` is this
 * file's own client-only nav-gating convention (no server equivalent — same
 * pattern as `feature:read`/`status:read` above); `help_article:write`/
 * `help_article:publish` mirror the REAL backend permissions
 * (`staffauth.PermHelpArticleWrite`/`PermHelpArticlePublish`,
 * admin-portal-specification.md §10) exactly, including their role scoping.
 *
 * `rbac:manage` backs Kirana-6ipq's staff pre-provisioning screen (invite by
 * email + grant/revoke role). Mirrors the REAL backend permission
 * (`staffauth.PermRBACManage`) exactly, including its role scoping — the
 * backend's `permissionMatrix` grants it to `RoleAdminOwner` only, no other
 * role, so it appears below on `admin_owner`'s row alone.
 *
 * `customer:delete` backs the Customer 360 "delete test account" action
 * (`DELETE /admin/customers/:userId`) — the console's first IRREVERSIBLE
 * action. Unlike the entries above, this permission's server-side role
 * scoping wasn't available to reconcile against at the time this was added
 * (the backend endpoint was built concurrently); it's placed on
 * `admin_owner` only, the same single-role scoping as `rbac:manage`, as the
 * conservative default for the most destructive action in this console.
 * Confirm against the real `permissionMatrix` and adjust if the backend
 * grants it more broadly.
 */
export type StaffPermission =
  | 'customer:read'
  | 'customer:reveal'
  | 'customer:delete'
  | 'billing:read'
  | 'billing:refund'
  | 'entitlement:override'
  | 'support:read'
  | 'ticket:read'
  | 'ticket:reply'
  | 'ticket:assign'
  | 'status:read'
  | 'status:write'
  | 'feature:read'
  | 'feature:write'
  | 'dashboards:read'
  | 'help_article:read'
  | 'help_article:write'
  | 'help_article:publish'
  | 'rbac:manage'

/**
 * Mirrors the illustrative permission matrix in admin-portal-specification.md
 * §15 — reconciled against the REAL, live backend matrix
 * (`backend/internal/modules/staffauth/rbac.go`'s `permissionMatrix`) during
 * the CRM-0 Wave C / TC1.2 billing-ops frontend integration: that matrix
 * grants `PermBillingRefund` to `support_l2` too (support escalation can
 * trigger a refund route, not just billing_ops), which this file's
 * `support_l2` row was missing — fixed here so the billing view's
 * client-side refund-button gating (`billing:refund`) doesn't hide an
 * action support_l2 staff are actually authorized to use server-side (the
 * "hiding an action for a role that can't use it is better UX than showing
 * it and 403ing" principle only holds if the client-side check matches the
 * server's own truth).
 */
export const ROLE_PERMISSIONS: Record<StaffRole, StaffPermission[]> = {
  // 'dashboards:read' (CRM-13) was reconciled onto support_l1/support_l2/
  // billing_ops/trust_safety here during CRM-13's own admin-console wiring:
  // the REAL backend matrix (staffauth/rbac.go's PermDashboardRead) grants
  // it to EVERY portal role ("every role may view ops dashboards" — that
  // permission's own doc comment) — this file's illustrative matrix had
  // drifted to only the 4 roles below data_curator, the same class of gap
  // this file's own header comment already documents fixing for
  // billing:refund/support_l2 during the TC1.2 integration.
  support_l1: ['customer:read', 'support:read', 'ticket:read', 'ticket:reply', 'dashboards:read'],
  support_l2: [
    'customer:read',
    'customer:reveal',
    'support:read',
    'ticket:read',
    'ticket:reply',
    'ticket:assign',
    'billing:read',
    'billing:refund',
    'dashboards:read',
  ],
  billing_ops: [
    'customer:read',
    'billing:read',
    'billing:refund',
    'entitlement:override',
    'dashboards:read',
  ],
  trust_safety: ['customer:read', 'customer:reveal', 'support:read', 'dashboards:read'],
  data_curator: ['dashboards:read', 'help_article:read', 'help_article:write'],
  release_manager: [
    'feature:read',
    'feature:write',
    'dashboards:read',
    'help_article:read',
    'help_article:write',
    'help_article:publish',
  ],
  platform_engineer: ['dashboards:read', 'status:read', 'status:write'],
  admin_owner: [
    'customer:read',
    'customer:reveal',
    'customer:delete',
    'billing:read',
    'billing:refund',
    'entitlement:override',
    'support:read',
    'ticket:read',
    'ticket:reply',
    'ticket:assign',
    'status:read',
    'status:write',
    'feature:read',
    'feature:write',
    'dashboards:read',
    'help_article:read',
    'help_article:write',
    'help_article:publish',
    'rbac:manage',
  ],
}
